/*eslint arrow-body-style: [0]*/
import { Icon } from "@iconify/react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { format } from "date-fns";
import * as _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useParams } from "react-router";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";

import { Box, HStack, VStack } from "../../components/utils";
import {
  useEmployees,
  useExportToExcel,
  useVestingTables,
} from "../../queries";
import { useCompanyStore } from "../../store/companyStore";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { EmployementStatus } from "../../types/Employee";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { formatDisplayDate } from "../../utils/date";
import { getFormattedValue } from "../../utils/string";
import {
  EmployeeGrantTransactionDetail,
  EmployeeVestingDetail,
  TriggerType,
} from "../../types/VestingTemplate";
import { Grant } from "../../types/Grant";

type GrantInfoCellProps = {
  grant: Grant;
  currency: string; // assuming you have a currency prop
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const VestingSchedule = () => {
  const { id } = useParams();
  const _id = id || "";
  const { data: _vestingSchedule, refetch } = useVestingTables(_id);
  const { data: _employees } = useEmployees();
  const employeeName = useMemo(
    () => (_employees || []).find((e: any) => e.id === _id)?.employeeName ?? "",
    [_id, _employees]
  );
  useEffect(() => {
    if (!_.isEmpty(_id)) {
      refetch();
    }
  }, [_id]);
  const [view, setView] = useState<"chart" | "table" | "ledger">("chart");
  const vestingScheduleTemplate = _vestingSchedule || [];
  const currency = getCurrencyType();
  const isFractional = vestingScheduleTemplate.some(
    (schedule) => schedule.isFractional
  );

  return (
    <Box className="p-4 bg-white ">
      <HStack className="justify-between">
        <VStack>
          <p className="font-semibold">Vesting Schedule</p>
        </VStack>
        <HStack className="gap-1 border border-gray-200 rounded p-0.5">
          <button
            title="Table — Row-by-row breakdown of every vesting event with grouping and filters"
            onClick={() => setView("table")}
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors ${
              view === "table"
                ? "bg-orange-501 text-white"
                : "text-gray-500 hover:text-orange-501"
            }`}
          >
            <Icon icon="mdi:table" height={14} />
            Table
          </button>
          <button
            title="Chart — Visual graphs of your vesting data across multiple chart types"
            onClick={() => setView("chart")}
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors ${
              view === "chart"
                ? "bg-orange-501 text-white"
                : "text-gray-500 hover:text-orange-501"
            }`}
          >
            <Icon icon="mdi:graph-bar" height={14} />
            Chart
          </button>
          <button
            title="Ledger — Chronological timeline of all transactions grouped by year and month with running balance"
            onClick={() => setView("ledger")}
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors ${
              view === "ledger"
                ? "bg-orange-501 text-white"
                : "text-gray-500 hover:text-orange-501"
            }`}
          >
            <Icon icon="mdi:receipt-text-outline" height={14} />
            Ledger
          </button>
        </HStack>
      </HStack>
      <Box className="mt-6 overflow-y-auto min-h-96">
        {view === "table" ? (
          <AGGridEmployeeScheduleTable
            currency={currency}
            isFractional={isFractional}
            data={vestingScheduleTemplate || []}
            employeeName={employeeName}
          />
        ) : view === "ledger" ? (
          <LedgerView
            currency={currency}
            isFractional={isFractional}
            data={vestingScheduleTemplate || []}
            employeeName={employeeName}
          />
        ) : (
          <ChartView
            data={vestingScheduleTemplate}
            currency={currency}
            isFractional={isFractional}
          />
        )}
      </Box>
    </Box>
  );
};

// ─── Chart View ───────────────────────────────────────────────────────────────

type ChartType = "stepper" | "grant-wise";

const CHART_TYPES: {
  value: ChartType;
  label: string;
  icon: string;
  description: string;
}[] = [
  {
    value: "stepper",
    label: "Step Chart",
    icon: "mdi:chart-timeline",
    description:
      "Stepped cumulative view — shows each event as an instant jump",
  },
  {
    value: "grant-wise",
    label: "Grant Wise",
    icon: "mdi:chart-donut-variant",
    description: "Cumulative donut + individual breakdown per grant",
  },
];

function fmt(val: number, currency: string, isFractional: boolean) {
  return getFormattedValue(val, currency, isFractional);
}

const POSITIVE_TRANSACTION_TYPES = [
  "VESTED",
  "ACCELERATION",
  "GRANTED",
  "YET_TO_VEST",
  "PROJECTED_VESTING",
];

const PROJECTED_TRANSACTION_TYPES = ["YET_TO_VEST", "PROJECTED_VESTING"];

const NEGATIVE_TRANSACTION_TYPES = [
  "CASHED_OUT",
  "STOCK_EXERCISE",
  "SURRENDERED",
  "LAPSED",
  "FORFEITED",
];

function normalizeTransactionType(transactionType?: string) {
  return (transactionType || "").replaceAll(" ", "_").toUpperCase();
}

function isPositiveTransaction(transactionType?: string) {
  return POSITIVE_TRANSACTION_TYPES.includes(
    normalizeTransactionType(transactionType)
  );
}

function isProjectedTransaction(transactionType?: string) {
  return PROJECTED_TRANSACTION_TYPES.includes(
    normalizeTransactionType(transactionType)
  );
}

function isNegativeTransaction(transactionType?: string) {
  return NEGATIVE_TRANSACTION_TYPES.includes(
    normalizeTransactionType(transactionType)
  );
}

function getDateKey(value?: string) {
  if (!value) return "";
  const rawValue = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }
  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime())
    ? rawValue
    : format(parsed, "yyyy-MM-dd");
}

function ChartView(props: {
  data: EmployeeGrantTransactionDetail[];
  currency: string;
  isFractional: boolean;
}) {
  const { data, currency, isFractional } = props;
  const [chartType, setChartType] = useState<ChartType>("stepper");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [debouncedDateFrom, setDebouncedDateFrom] = useState("");
  const [debouncedDateTo, setDebouncedDateTo] = useState("");
  const [grantIdFilter, setGrantIdFilter] = useState<string>("all");
  const [showGrantWiseInfo, setShowGrantWiseInfo] = useState(false);
  const [showStepperInfo, setShowStepperInfo] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDateFrom(dateFrom);
      setDebouncedDateTo(dateTo);
    }, 1000);
    return () => clearTimeout(timer);
  }, [dateFrom, dateTo]);

  const grantIds = useMemo(() => {
    const ids = Array.from(
      new Set(
        data
          .filter((r) => r.date !== "TOTAL" && r.identifier)
          .map((r) => r.identifier!)
      )
    ).sort();
    return ids;
  }, [data]);

  const rows = useMemo(() => {
    const fromDateKey = getDateKey(debouncedDateFrom);
    const toDateKey = getDateKey(debouncedDateTo);

    return data
      .filter((r) => r.date !== "TOTAL")
      .filter((r) => {
        const rowDateKey = getDateKey(r.date);
        return (
          (!fromDateKey || rowDateKey >= fromDateKey) &&
          (!toDateKey || rowDateKey <= toDateKey)
        );
      })
      .filter((r) => grantIdFilter === "all" || r.identifier === grantIdFilter)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data, debouncedDateFrom, debouncedDateTo, grantIdFilter]);

  // ── Shared label formatter
  const fmtVal = (v: number) => fmt(Math.abs(v), currency, isFractional);
  const val = (v: number) => (isFractional ? parseFloat(v.toFixed(4)) : v);

  // ── 1. Stacked Bar — options per event, grouped by transaction type
  const stackedBarData = useMemo(() => {
    const labels = rows.map((r) => formatDisplayDate(r.date));
    const types = [
      { key: "VESTED", label: "Vested", color: "rgb(50, 145, 220)" },
      { key: "ACCELERATION", label: "Accelerated", color: "rgb(175, 60, 210)" },
      { key: "GRANTED", label: "Granted", color: "rgb(52, 168, 110)" },
      { key: "YET_TO_VEST", label: "Scheduled", color: "rgb(195, 138, 10)" },
      { key: "CASHED_OUT", label: "Cashed Out", color: "rgb(234, 88, 12)" },
      { key: "STOCK_EXERCISE", label: "Exercised", color: "rgb(115, 80, 220)" },
      { key: "SURRENDERED", label: "Surrendered", color: "rgb(215, 70, 60)" },
      { key: "LAPSED", label: "Lapsed", color: "rgb(110, 118, 132)" },
      { key: "FORFEITED", label: "Forfeited", color: "rgb(148, 74, 74)" },
    ];
    return {
      labels,
      datasets: types.map((t) => ({
        label: t.label,
        data: rows.map((r) =>
          r.transactionType === t.key ? val(Math.abs(r.options)) : 0
        ),
        backgroundColor: `${t.color}cc`,
        borderColor: t.color,
        borderWidth: 1,
        borderRadius: 3,
        stack: "stack",
      })),
    };
  }, [rows, isFractional]);

  const stackedBarOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) =>
            item.parsed.y > 0
              ? ` ${item.dataset.label}: ${fmtVal(item.parsed.y)}`
              : "",
        },
        filter: (item) => (item.parsed.y ?? 0) > 0,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { font: { size: 10 }, maxRotation: 45 },
        grid: { color: "#f0f0f0" },
      },
      y: {
        stacked: true,
        ticks: {
          font: { size: 11 },
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f0f0f0", lineWidth: 1 },
        title: { display: true, text: "No. of Options", font: { size: 11 } },
      },
    },
  };

  // ── 2. Area Chart — cumulative flows
  const areaData = useMemo(() => {
    const labels = rows.map((r) => formatDisplayDate(r.date));
    return {
      labels,
      datasets: [
        {
          label: "Total Granted",
          data: rows.map((r) => val(r.totalGranted)),
          borderColor: "rgb(52, 168, 110)",
          backgroundColor: "rgba(52, 168, 110, 0.08)",
          borderWidth: 2,
          borderDash: [6, 3],
          fill: false,
          tension: 0.3,
        },
        {
          label: "Cumulative Vested",
          data: rows.map((r) => val(r.currentVested)),
          borderColor: "rgb(50, 145, 220)",
          backgroundColor: "rgba(50, 145, 220, 0.15)",
          borderWidth: 2,
          fill: true,
          tension: 0.3,
        },
        {
          label: "Net Vested",
          data: rows.map((r) => val(r.netVested)),
          borderColor: "rgb(115, 80, 220)",
          backgroundColor: "rgba(115, 80, 220, 0.10)",
          borderWidth: 2,
          fill: true,
          tension: 0.3,
        },
        {
          label: "Cumulative Exercised",
          data: rows.map((r) => val(r.netStockExercised)),
          borderColor: "rgb(234, 88, 12)",
          backgroundColor: "rgba(234, 88, 12, 0.08)",
          borderWidth: 2,
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [rows, isFractional]);

  const areaOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { point: { radius: 0, hoverRadius: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) => ` ${item.dataset.label}: ${fmtVal(item.parsed.y)}`,
          afterBody: (items) => {
            const r = rows[items[0]?.dataIndex ?? -1];
            if (!r) return [];
            const isPositive = isPositiveTransaction(r.transactionType);
            const txType = r.transactionType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            return [
              ` Transaction: ${txType}`,
              ` Options in this event: ${isPositive ? "+" : "−"}${fmtVal(
                Math.abs(val(r.options))
              )}`,
            ];
          },
        },
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45 },
        grid: { color: "#f0f0f0" },
      },
      y: {
        ticks: {
          font: { size: 11 },
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f0f0f0" },
        title: { display: true, text: "No. of Options", font: { size: 11 } },
      },
    },
  };

  // ── 5. Overview — yearly stacked bar + dashed granted ceiling line (mixed)
  const overviewData = useMemo(() => {
    // Aggregate by year
    const byYear: Record<
      string,
      {
        netVested: number;
        exercised: number;
        cashedOut: number;
        lapsed: number;
        forfeited: number;
        scheduled: number;
        granted: number;
      }
    > = {};
    for (const r of rows) {
      const year = String(new Date(r.date).getFullYear());
      if (!byYear[year])
        byYear[year] = {
          netVested: 0,
          exercised: 0,
          cashedOut: 0,
          lapsed: 0,
          forfeited: 0,
          scheduled: 0,
          granted: 0,
        };
      const b = byYear[year];
      // totalGranted is cumulative — take the max seen in that year
      if (val(r.totalGranted) > b.granted) b.granted = val(r.totalGranted);
      if (
        r.transactionType === "VESTED" ||
        r.transactionType === "ACCELERATION"
      ) {
        b.netVested += val(Math.abs(r.options));
      } else if (r.transactionType === "STOCK_EXERCISE") {
        b.exercised += val(Math.abs(r.options));
      } else if (r.transactionType === "CASHED_OUT") {
        b.cashedOut += val(Math.abs(r.options));
      } else if (r.transactionType === "FORFEITED") {
        b.forfeited += val(Math.abs(r.options));
      } else if (
        r.transactionType === "LAPSED" ||
        r.transactionType === "SURRENDERED"
      ) {
        b.lapsed += val(Math.abs(r.options));
      } else if (isProjectedTransaction(r.transactionType)) {
        b.scheduled += val(Math.abs(r.options));
      }
    }
    const years = Object.keys(byYear).sort();
    return {
      labels: years,
      datasets: [
        {
          type: "bar" as const,
          label: "Net Vested",
          data: years.map((y) => byYear[y].netVested),
          backgroundColor: "rgba(50, 145, 220, 0.85)",
          borderColor: "rgb(50, 145, 220)",
          borderWidth: 0,
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Exercised",
          data: years.map((y) => byYear[y].exercised),
          backgroundColor: "rgba(115, 80, 220, 0.85)",
          borderColor: "rgb(115, 80, 220)",
          borderWidth: 0,
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Cashed Out",
          data: years.map((y) => byYear[y].cashedOut),
          backgroundColor: "rgba(234, 88, 12, 0.85)",
          borderColor: "rgb(234, 88, 12)",
          borderWidth: 0,
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Lapsed / Surrendered",
          data: years.map((y) => byYear[y].lapsed),
          backgroundColor: "rgba(215, 70, 60, 0.85)",
          borderColor: "rgb(215, 70, 60)",
          borderWidth: 0,
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Forfeited",
          data: years.map((y) => byYear[y].forfeited),
          backgroundColor: "rgba(148, 74, 74, 0.85)",
          borderColor: "rgb(148, 74, 74)",
          borderWidth: 0,
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Scheduled",
          data: years.map((y) => byYear[y].scheduled),
          backgroundColor: "rgba(195, 138, 10, 0.5)",
          borderColor: "rgb(195, 138, 10)",
          borderWidth: 1,
          borderDash: [4, 2],
          borderRadius: 3,
          stack: "s",
          order: 2,
        },
        {
          type: "line" as const,
          label: "Total Granted",
          data: years.map((y) => byYear[y].granted),
          borderColor: "rgb(52, 168, 110)",
          backgroundColor: "transparent",
          borderWidth: 2,
          borderDash: [6, 3],
          tension: 0,
          fill: false,
          order: 1,
        },
      ] as any[],
    };
  }, [rows, isFractional]);

  const overviewOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { point: { radius: 0, hoverRadius: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 11 },
          padding: 14,
          usePointStyle: false,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#fff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        titleColor: "#111",
        bodyColor: "#555",
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 11 },
        padding: 10,
        callbacks: {
          title: (items) => `Year ${items[0]?.label ?? ""}`,
          label: (item) =>
            item.parsed.y > 0
              ? ` ${item.dataset.label}: ${fmtVal(item.parsed.y)}`
              : "",
        },
        filter: (item) => (item.parsed.y ?? 0) > 0,
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        stacked: true,
        ticks: { font: { size: 10 }, color: "#9ca3af" },
        grid: { display: false },
      },
      y: {
        stacked: true,
        ticks: {
          font: { size: 10 },
          color: "#9ca3af",
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#e8e8e8", lineWidth: 1 },
        title: { display: false },
      },
    },
  };

  // ── 7. Grant Wise — cumulative donut (left) + per-grant donuts (right)
  const DONUT_SEGS = [
    {
      keys: ["VESTED", "ACCELERATION"],
      label: "Net Vested",
      color: "rgba(50, 145, 220, 0.88)",
    },
    {
      keys: ["STOCK_EXERCISE"],
      label: "Exercised",
      color: "rgba(115, 80, 220, 0.88)",
    },
    {
      keys: ["CASHED_OUT"],
      label: "Cashed Out",
      color: "rgba(234, 88, 12, 0.88)",
    },
    {
      keys: ["LAPSED", "SURRENDERED"],
      label: "Lapsed / Surrendered",
      color: "rgba(215, 70, 60, 0.88)",
    },
    {
      keys: ["FORFEITED"],
      label: "Forfeited",
      color: "rgba(148, 74, 74, 0.88)",
    },
    {
      keys: ["YET_TO_VEST"],
      label: "Scheduled",
      color: "rgba(195, 138, 10, 0.55)",
    },
  ];

  const grantWiseData = useMemo(() => {
    type GrantBucket = { totals: number[]; totalGranted: number };
    const byGrant: Record<string, GrantBucket> = {};
    for (const r of rows) {
      const id = r.identifier ?? "Unknown";
      if (!byGrant[id])
        byGrant[id] = {
          totals: Array(DONUT_SEGS.length).fill(0),
          totalGranted: 0,
        };
      const g = byGrant[id];
      if (r.transactionType === "GRANTED")
        g.totalGranted += val(Math.abs(r.options));
      DONUT_SEGS.forEach((seg, i) => {
        if (seg.keys.includes(r.transactionType))
          g.totals[i] += val(Math.abs(r.options));
      });
    }
    const grants = Object.keys(byGrant).sort();
    const cumTotals = Array(DONUT_SEGS.length).fill(0) as number[];
    let cumTotalGranted = 0;
    for (const g of Object.values(byGrant)) {
      g.totals.forEach((v, i) => {
        cumTotals[i] += v;
      });
      cumTotalGranted += g.totalGranted;
    }
    return { byGrant, grants, cumTotals, cumTotalGranted };
  }, [rows, isFractional]);

  const OTHER_COLOR = "rgba(156, 163, 175, 0.55)";

  const donutBaseDataset = (data: number[], totalGranted?: number) => {
    const segSum = data.reduce((a, b) => a + b, 0);
    const other =
      totalGranted !== undefined ? Math.max(0, totalGranted - segSum) : 0;
    return {
      data: other > 0 ? [...data, other] : data,
      backgroundColor:
        other > 0
          ? [...DONUT_SEGS.map((s) => s.color), OTHER_COLOR]
          : DONUT_SEGS.map((s) => s.color),
      borderWidth: 0,
      hoverOffset: 5,
    };
  };

  const donutLabels = () => [...DONUT_SEGS.map((s) => s.label), "Granted"];

  const donutOptions = (small = false): ChartOptions<"doughnut"> => ({
    responsive: true,
    maintainAspectRatio: true,
    cutout: small ? "60%" : "68%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        titleColor: "#111",
        bodyColor: "#555",
        titleFont: { size: 11, weight: "bold" },
        bodyFont: { size: 11 },
        padding: 8,
        callbacks: {
          label: (item) =>
            item.parsed > 0 ? ` ${item.label}: ${fmtVal(item.parsed)}` : "",
        },
        filter: (item) => (item.parsed ?? 0) > 0,
      },
    },
  });

  // ── 6. Ledger — dual-axis: cumulative lines (left) + transaction delta bars (right)
  const dualAxisData = useMemo(() => {
    const labels = rows.map((r) => formatDisplayDate(r.date));
    return {
      labels,
      datasets: [
        {
          type: "line" as const,
          label: "Net Granted",
          data: rows.map((r) => val(r.totalGranted)),
          borderColor: "rgb(99, 102, 241)",
          backgroundColor: "transparent",
          borderWidth: 2,
          borderDash: [7, 4],
          tension: 0.35,
          yAxisID: "yLeft",
          order: 1,
        },
        {
          type: "line" as const,
          label: "Vested Units",
          data: rows.map((r) => val(r.currentVested)),
          borderColor: "rgb(34, 197, 94)",
          backgroundColor: "rgba(34, 197, 94, 0.06)",
          borderWidth: 2,
          fill: false,
          tension: 0.35,
          yAxisID: "yLeft",
          order: 1,
        },
        {
          type: "bar" as const,
          label: "Transaction Delta",
          data: rows.map((r) => {
            if (isPositiveTransaction(r.transactionType))
              return val(Math.abs(r.options));
            if (isNegativeTransaction(r.transactionType))
              return -val(Math.abs(r.options));
            return 0;
          }),
          backgroundColor: rows.map((r) =>
            isPositiveTransaction(r.transactionType)
              ? "rgba(34, 197, 94, 0.25)"
              : "rgba(239, 68, 68, 0.25)"
          ),
          borderColor: rows.map((r) =>
            isPositiveTransaction(r.transactionType)
              ? "rgba(34, 197, 94, 0.7)"
              : "rgba(239, 68, 68, 0.7)"
          ),
          borderWidth: 1,
          borderRadius: 2,
          yAxisID: "yRight",
          order: 2,
        },
      ] as any[],
    };
  }, [rows, isFractional]);

  const dualAxisOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { point: { radius: 0, hoverRadius: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#fff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        titleColor: "#111",
        bodyColor: "#555",
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 11 },
        padding: 10,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) => {
            const v = item.parsed.y;
            if (v === 0) return "";
            const prefix =
              item.dataset.label === "Transaction Delta" && v > 0 ? "+" : "";
            return ` ${item.dataset.label}: ${prefix}${fmtVal(v)}`;
          },
        },
        filter: (item) => (item.parsed.y ?? 0) !== 0,
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45, color: "#9ca3af" },
        grid: { color: "#f3f4f6" },
      },
      yLeft: {
        type: "linear",
        position: "left",
        ticks: {
          font: { size: 10 },
          color: "#9ca3af",
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f3f4f6" },
        title: {
          display: true,
          text: "Cumulative Units",
          font: { size: 10 },
          color: "#9ca3af",
        },
      },
      yRight: {
        type: "linear",
        position: "right",
        ticks: {
          font: { size: 10 },
          color: "#9ca3af",
          callback: (v) => {
            const n = Number(v);
            return (n >= 0 ? "+" : "") + fmtVal(n);
          },
        },
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: "Delta",
          font: { size: 10 },
          color: "#9ca3af",
        },
      },
    },
  };

  // ── 3. Waterfall — running net vested delta per event
  const waterfallData = useMemo(() => {
    // Only include rows that affect net vested
    const relevant = rows.filter((r) =>
      [
        "VESTED",
        "ACCELERATION",
        "GRANTED",
        "CASHED_OUT",
        "STOCK_EXERCISE",
        "SURRENDERED",
        "LAPSED",
        "FORFEITED",
      ].includes(r.transactionType)
    );
    const labels = relevant.map((r) => formatDisplayDate(r.date));
    // Each bar is a floating bar [start, end] to create waterfall effect
    let running = 0;
    const floatingData: [number, number][] = [];
    const colors: string[] = [];
    const deltas: number[] = [];
    for (const r of relevant) {
      let delta = 0;
      if (isPositiveTransaction(r.transactionType)) {
        delta = val(Math.abs(r.options));
      } else {
        delta = -val(Math.abs(r.options));
      }
      floatingData.push([running, running + delta]);
      colors.push(
        delta >= 0 ? "rgba(50, 145, 220, 0.85)" : "rgba(215, 70, 60, 0.85)"
      );
      deltas.push(delta);
      running += delta;
    }
    return {
      labels,
      datasets: [
        {
          label: "Change in Options",
          data: floatingData as any,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace("0.85", "1")),
          borderWidth: 1,
          borderRadius: 3,
        },
      ],
      _deltas: deltas,
      _relevant: relevant,
    };
  }, [rows, isFractional]);

  const waterfallOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) => {
            const delta = waterfallData._deltas[item.dataIndex];
            const end = (item.raw as [number, number])[1];
            return [
              ` ${delta >= 0 ? "+" : ""}${fmtVal(delta)} options`,
              ` Running balance: ${fmtVal(end)}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45 },
        grid: { color: "#f0f0f0" },
      },
      y: {
        ticks: {
          font: { size: 11 },
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f0f0f0" },
        title: {
          display: true,
          text: "Cumulative Net Options",
          font: { size: 11 },
        },
      },
    },
  };

  // ── 4. Trend Lines — granted ceiling + key cumulative metrics
  const lineData = useMemo(() => {
    const labels = rows.map((r) => formatDisplayDate(r.date));
    return {
      labels,
      datasets: [
        {
          label: "Total Granted (ceiling)",
          data: rows.map((r) => val(r.totalGranted)),
          borderColor: "rgb(52, 168, 110)",
          backgroundColor: "transparent",
          borderWidth: 2,
          borderDash: [8, 4],
          tension: 0,
        },
        {
          label: "Net Vested",
          data: rows.map((r) => val(r.netVested)),
          borderColor: "rgb(50, 145, 220)",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.25,
        },
        {
          label: "Cumulative Exercised",
          data: rows.map((r) => val(r.netStockExercised)),
          borderColor: "rgb(115, 80, 220)",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.25,
        },
        {
          label: "Cumulative Cashed Out",
          data: rows.map((r) => val(r.netCashedOut)),
          borderColor: "rgb(234, 88, 12)",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.25,
        },
      ],
    };
  }, [rows, isFractional]);

  const lineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { point: { radius: 0, hoverRadius: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) => ` ${item.dataset.label}: ${fmtVal(item.parsed.y)}`,
          afterBody: (items) => {
            const r = rows[items[0]?.dataIndex ?? -1];
            if (!r) return [];
            const isPositive = isPositiveTransaction(r.transactionType);
            const txType = r.transactionType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            return [
              ` Transaction: ${txType}`,
              ` Options in this event: ${isPositive ? "+" : "−"}${fmtVal(
                Math.abs(val(r.options))
              )}`,
            ];
          },
        },
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45 },
        grid: { color: "#f0f0f0" },
      },
      y: {
        ticks: {
          font: { size: 11 },
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f0f0f0" },
        title: { display: true, text: "No. of Options", font: { size: 11 } },
      },
    },
  };

  // ── 8. Stepper — stepped cumulative view (instant jumps at each event)
  const stepperData = useMemo(() => {
    const labels = rows.map((r) => formatDisplayDate(r.date));
    return {
      labels,
      datasets: [
        {
          label: "Total Granted",
          data: rows.map((r) => val(r.totalGranted)),
          borderColor: "rgb(52, 168, 110)",
          backgroundColor: "rgba(52, 168, 110, 0.07)",
          borderWidth: 2,
          borderDash: [6, 3],
          fill: false,
          stepped: true,
        },
        {
          label: "Cumulative Vested",
          data: rows.map((r) => val(r.currentVested)),
          borderColor: "rgb(50, 145, 220)",
          backgroundColor: "rgba(50, 145, 220, 0.13)",
          borderWidth: 2,
          fill: true,
          stepped: true,
        },
        {
          label: "Net Vested",
          data: rows.map((r) => val(r.netVested)),
          borderColor: "rgb(115, 80, 220)",
          backgroundColor: "rgba(115, 80, 220, 0.09)",
          borderWidth: 2,
          fill: true,
          stepped: true,
        },
        {
          label: "Cumulative Exercised",
          data: rows.map((r) => val(r.netStockExercised)),
          borderColor: "rgb(234, 88, 12)",
          backgroundColor: "rgba(234, 88, 12, 0.07)",
          borderWidth: 2,
          fill: true,
          stepped: true,
        },
        {
          label: "Cumulative Cashed Out",
          data: rows.map((r) => val(r.netCashedOut)),
          borderColor: "rgb(8, 145, 178)",
          backgroundColor: "rgba(8, 145, 178, 0.07)",
          borderWidth: 2,
          fill: true,
          stepped: true,
        },
      ],
    };
  }, [rows, isFractional]);

  const stepperOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { point: { radius: 0, hoverRadius: 0 } },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 }, padding: 14 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (items) => items[0]?.label ?? "",
          label: (item) =>
            item.parsed.y > 0
              ? ` ${item.dataset.label}: ${fmtVal(item.parsed.y)}`
              : "",
          afterBody: (items) => {
            const r = rows[items[0]?.dataIndex ?? -1];
            if (!r) return [];
            const isPositive = isPositiveTransaction(r.transactionType);
            const txType = r.transactionType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            return [
              ` Transaction: ${txType}`,
              ` Options in this event: ${isPositive ? "+" : "−"}${fmtVal(
                Math.abs(val(r.options))
              )}`,
            ];
          },
        },
        filter: (item) => (item.parsed.y ?? 0) > 0,
      },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        ticks: { font: { size: 10 }, maxRotation: 45 },
        grid: { color: "#f0f0f0" },
      },
      y: {
        ticks: {
          font: { size: 11 },
          callback: (v) => fmtVal(Number(v)),
        },
        grid: { color: "#f0f0f0" },
        title: { display: true, text: "No. of Options", font: { size: 11 } },
      },
    },
  };

  return (
    <VStack className="w-full gap-4">
      {/* Date range filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Icon icon="mdi:calendar-range" height={14} className="text-gray-400" />
        <span className="text-[11px] text-gray-500 font-medium">
          Filter by date:
        </span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 outline-none focus:border-orange-501"
        />
        <span className="text-[11px] text-gray-400">to</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 outline-none focus:border-orange-501"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
            }}
            className="text-[11px] text-orange-501 hover:underline"
          >
            Clear
          </button>
        )}
        {grantIds.length > 1 && (
          <>
            <span className="text-[11px] text-gray-400 ml-2">|</span>
            <Icon
              icon="mdi:badge-account-outline"
              height={14}
              className="text-gray-400"
            />
            <span className="text-[11px] text-gray-500 font-medium">
              Grant:
            </span>
            <select
              value={grantIdFilter}
              onChange={(e) => setGrantIdFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 outline-none focus:border-orange-501 bg-white"
            >
              <option value="all">All</option>
              {grantIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
            {grantIdFilter !== "all" && (
              <button
                onClick={() => setGrantIdFilter("all")}
                className="text-[11px] text-orange-501 hover:underline"
              >
                Clear
              </button>
            )}
          </>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
          <Icon
            icon="mdi:chart-timeline-variant-shimmer"
            height={40}
            className="opacity-30"
          />
          <p className="text-sm">
            {dateFrom || dateTo || grantIdFilter !== "all"
              ? "No transactions match the selected filters"
              : "No vesting data available"}
          </p>
        </div>
      ) : (
        <>
          {/* Chart type selector */}
          <div className="flex items-center gap-2 flex-wrap">
            {CHART_TYPES.map((ct) => (
              <button
                key={ct.value}
                onClick={() => setChartType(ct.value)}
                title={ct.description}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  chartType === ct.value
                    ? "bg-orange-501 text-white border-orange-501"
                    : "bg-white text-gray-500 border-gray-200 hover:border-orange-501 hover:text-orange-501"
                }`}
              >
                <Icon icon={ct.icon} height={13} />
                {ct.label}
              </button>
            ))}
            <span className="text-[11px] text-gray-400 ml-1 hidden sm:block">
              {CHART_TYPES.find((c) => c.value === chartType)?.description}
            </span>
          </div>

          {/* Chart canvas */}
          {chartType !== "grant-wise" ? (
            <div>
              <div style={{ height: 340, position: "relative" }}>
                {chartType === "stepper" && (
                  <Line
                    key={`stepper-${debouncedDateFrom}-${debouncedDateTo}-${grantIdFilter}`}
                    data={stepperData}
                    options={stepperOptions}
                  />
                )}
              </div>
              {/* ── Stepper How to Read ── */}
              {chartType === "stepper" && (
                <div className="mt-1 flex justify-end px-1">
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-500 transition-colors"
                      onClick={() => setShowStepperInfo((v) => !v)}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        className="w-4 h-4"
                      />
                      How to read
                    </button>
                    {showStepperInfo && (
                      <div className="absolute right-0 bottom-7 z-50 w-72 rounded-xl bg-white border border-gray-200 shadow-xl p-4 text-[11px] text-gray-600 leading-relaxed">
                        <button
                          type="button"
                          className="absolute top-2 right-2 text-gray-300 hover:text-gray-500"
                          onClick={() => setShowStepperInfo(false)}
                        >
                          <Icon icon="mdi:close" className="w-3.5 h-3.5" />
                        </button>
                        <p className="font-semibold text-gray-700 mb-2 text-xs">
                          How to read this chart
                        </p>
                        <ul className="space-y-1.5 list-none">
                          <li className="flex gap-2">
                            <span
                              className="mt-1 flex-shrink-0 inline-block"
                              style={{
                                width: 16,
                                height: 2,
                                borderTop: "2px dashed rgb(52,168,110)",
                              }}
                            />
                            <span>
                              <b>Total Granted</b> — the running total of
                              options granted over time. Steps up when a new
                              grant is made.
                            </span>
                          </li>
                          <li className="flex gap-2">
                            <span
                              className="mt-1 flex-shrink-0 inline-block"
                              style={{
                                width: 16,
                                height: 2,
                                borderTop: "2px solid rgb(50,145,220)",
                              }}
                            />
                            <span>
                              <b>Cumulative Vested</b> — all options that have
                              vested so far (gross, before any exercises or
                              cashouts).
                            </span>
                          </li>
                          <li className="flex gap-2">
                            <span
                              className="mt-1 flex-shrink-0 inline-block"
                              style={{
                                width: 16,
                                height: 2,
                                borderTop: "2px solid rgb(115,80,220)",
                              }}
                            />
                            <span>
                              <b>Net Vested</b> — vested options minus exercised
                              and cashed-out ones. What remains available to the
                              employee.
                            </span>
                          </li>
                          <li className="flex gap-2">
                            <span
                              className="mt-1 flex-shrink-0 inline-block"
                              style={{
                                width: 16,
                                height: 2,
                                borderTop: "2px solid rgb(234,88,12)",
                              }}
                            />
                            <span>
                              <b>Cumulative Exercised</b> — total options
                              converted to stock since grant date.
                            </span>
                          </li>
                          <li className="flex gap-2">
                            <span
                              className="mt-1 flex-shrink-0 inline-block"
                              style={{
                                width: 16,
                                height: 2,
                                borderTop: "2px solid rgb(8,145,178)",
                              }}
                            />
                            <span>
                              <b>Cumulative Cashed Out</b> — total options
                              settled for cash since grant date.
                            </span>
                          </li>
                        </ul>
                        <p className="mt-2 text-gray-400">
                          Each line steps at the exact date a transaction
                          occurred. Hover over the chart to see values at any
                          point in time.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Grant Wise layout ── */
            <div className="w-full">
              {/* Two-panel layout: frozen summary left + scrollable grants right */}
              <div className="flex gap-0 overflow-visible rounded-lg border border-gray-100">
                {/* ── LEFT: frozen portfolio summary ── */}
                <div
                  className="flex-shrink-0 flex flex-col gap-3 p-4 bg-gray-50 border-r border-gray-100"
                  style={{ width: 280 }}
                >
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                    All Grants
                  </p>
                  <div className="flex justify-center">
                    <div
                      className="relative"
                      style={{ width: 200, height: 200 }}
                    >
                      <Doughnut
                        data={{
                          labels: donutLabels(),
                          datasets: [
                            donutBaseDataset(
                              grantWiseData.cumTotals,
                              grantWiseData.cumTotalGranted
                            ),
                          ],
                        }}
                        options={donutOptions(false)}
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-[9px] text-gray-400 leading-tight">
                          Granted
                        </span>
                        <span className="text-sm font-bold text-gray-700 tabular-nums leading-tight">
                          {fmtVal(grantWiseData.cumTotalGranted)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {DONUT_SEGS.map((seg, i) =>
                      grantWiseData.cumTotals[i] > 0 ? (
                        <div
                          key={seg.label}
                          className="flex items-center justify-between gap-1 text-[10px]"
                        >
                          <span className="text-gray-500 truncate">
                            {seg.label}
                          </span>
                          <span className="text-gray-700 font-semibold tabular-nums whitespace-nowrap">
                            {fmtVal(grantWiseData.cumTotals[i])}
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>

                {/* ── RIGHT: per-grant donuts — horizontal scroll ── */}
                <div className="flex-1 overflow-x-auto min-w-0">
                  <div
                    className="flex gap-0 h-full"
                    style={{
                      minWidth: `${
                        grantWiseData.grants.filter(
                          (id) => grantWiseData.byGrant[id].totalGranted > 0
                        ).length * 220
                      }px`,
                    }}
                  >
                    {grantWiseData.grants.map((grantId) => {
                      const g = grantWiseData.byGrant[grantId];
                      if (g.totalGranted <= 0) return null;
                      return (
                        <div
                          key={grantId}
                          className="flex flex-col items-center gap-2 p-4 border-r border-gray-100 last:border-r-0 flex-shrink-0"
                          style={{ width: 220 }}
                        >
                          <p className="text-xs font-semibold text-gray-700 self-start font-mono">
                            {grantId}
                          </p>
                          <div
                            className="relative"
                            style={{ width: 160, height: 160 }}
                          >
                            <Doughnut
                              data={{
                                labels: donutLabels(),
                                datasets: [
                                  donutBaseDataset(g.totals, g.totalGranted),
                                ],
                              }}
                              options={donutOptions(true)}
                            />
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-[8px] text-gray-400 leading-tight">
                                Granted
                              </span>
                              <span className="text-xs font-bold text-gray-700 tabular-nums leading-tight">
                                {fmtVal(g.totalGranted)}
                              </span>
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-400 self-start">
                            Granted:{" "}
                            <span className="font-semibold text-gray-600">
                              {fmtVal(g.totalGranted)}
                            </span>
                          </p>
                          <div className="flex flex-col gap-1 w-full">
                            {DONUT_SEGS.map((seg, i) =>
                              g.totals[i] > 0 ? (
                                <div
                                  key={seg.label}
                                  className="flex items-center justify-between gap-1 text-[10px]"
                                >
                                  <span className="text-gray-400 truncate flex-1">
                                    {seg.label}
                                  </span>
                                  <span className="text-gray-600 font-medium tabular-nums">
                                    {fmtVal(g.totals[i])}
                                  </span>
                                </div>
                              ) : null
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Empty state if no grants */}
                    {grantWiseData.grants.every(
                      (id) => grantWiseData.byGrant[id].totalGranted <= 0
                    ) && (
                      <div className="flex items-center justify-center flex-1 text-xs text-gray-300 py-8">
                        No grant data
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* ── Legend + How to Read ── */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1">
                {/* Colour legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {DONUT_SEGS.map((seg) => (
                    <span
                      key={seg.label}
                      className="flex items-center gap-1.5 text-[11px] text-gray-500"
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: seg.color }}
                      />
                      {seg.label}
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: OTHER_COLOR }}
                    />
                    Granted (total)
                  </span>
                </div>
                {/* Info icon + popover */}
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-500 transition-colors"
                    onClick={() => setShowGrantWiseInfo((v) => !v)}
                  >
                    <Icon icon="mdi:information-outline" className="w-4 h-4" />
                    How to read
                  </button>
                  {showGrantWiseInfo && (
                    <div className="absolute right-0 bottom-7 z-50 w-72 rounded-xl bg-white border border-gray-200 shadow-xl p-4 text-[11px] text-gray-600 leading-relaxed">
                      <button
                        type="button"
                        className="absolute top-2 right-2 text-gray-300 hover:text-gray-500"
                        onClick={() => setShowGrantWiseInfo(false)}
                      >
                        <Icon icon="mdi:close" className="w-3.5 h-3.5" />
                      </button>
                      <p className="font-semibold text-gray-700 mb-2 text-xs">
                        How to read this chart
                      </p>
                      <ul className="space-y-1.5 list-none">
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(50,145,220,0.88)" }}
                          />
                          <span>
                            <b>Net Vested</b> — options that have vested (incl.
                            accelerations).
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(115,80,220,0.88)" }}
                          />
                          <span>
                            <b>Exercised</b> — vested options converted to
                            stock.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(234,88,12,0.88)" }}
                          />
                          <span>
                            <b>Cashed Out</b> — options settled for cash.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(215,70,60,0.88)" }}
                          />
                          <span>
                            <b>Lapsed / Surrendered</b> — options that expired
                            or were given up.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(148,74,74,0.88)" }}
                          />
                          <span>
                            <b>Forfeited</b> — options cancelled due to
                            termination or conditions not met.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: "rgba(195,138,10,0.55)" }}
                          />
                          <span>
                            <b>Scheduled</b> — options yet to vest on future
                            dates.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span
                            className="mt-0.5 flex-shrink-0 w-2 h-2 rounded-full"
                            style={{ background: OTHER_COLOR }}
                          />
                          <span>
                            <b>Granted</b> — the total options awarded in the
                            grant (shown as the donut centre and the grey arc).
                          </span>
                        </li>
                      </ul>
                      <p className="mt-2 text-gray-400">
                        The <b>centre number</b> of each donut is the total
                        granted for that grant. The coloured arcs show how those
                        options have been distributed across transaction types.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </VStack>
  );
}

// ─── Ledger View ──────────────────────────────────────────────────────────────

const LEDGER_FILTER_TABS = [
  { value: "all", label: "ALL" },
  { value: "vested", label: "VESTED ACTIVITIES" },
  { value: "acceleration", label: "ACCELERATIONS" },
] as const;

type LedgerFilterValue = typeof LEDGER_FILTER_TABS[number]["value"];

function getTransactionIcon(transactionType: string, triggerType?: string) {
  switch (transactionType) {
    case "VESTED":
      return triggerType === "EVENT"
        ? { icon: "mdi:star-circle", color: "rgb(80, 175, 245)" }
        : { icon: "mdi:clock-check", color: "rgb(80, 175, 245)" };
    case "ACCELERATION":
      return { icon: "mdi:lightning-bolt-circle", color: "rgb(160, 120, 245)" };
    case "GRANTED":
      return { icon: "mdi:check-circle", color: "rgb(122, 208, 150)" };
    case "LAPSED":
      return { icon: "mdi:close-circle", color: "rgb(150, 150, 150)" };
    case "SURRENDERED":
      return { icon: "mdi:minus-circle", color: "rgb(245, 108, 108)" };
    case "CASHED_OUT":
      return { icon: "mdi:cash-multiple", color: "rgb(80, 175, 245)" };
    case "STOCK_EXERCISE":
      return { icon: "mdi:chart-line", color: "rgb(160, 120, 245)" };
    case "YET_TO_VEST":
    case "PROJECTED_VESTING":
      return { icon: "mdi:clock-outline", color: "rgb(245, 184, 50)" };
    case "FORFEITED":
      return { icon: "mdi:cancel", color: "rgb(148, 74, 74)" };
    default:
      return { icon: "mdi:circle-outline", color: "rgb(150, 150, 150)" };
  }
}

function getEventTitle(row: EmployeeGrantTransactionDetail): string {
  if (
    row.transactionType === "VESTED" &&
    row.triggerType === TriggerType.EVENT &&
    row.milestoneName
  ) {
    return row.milestoneName;
  }
  if (row.transactionType === "LAPSED" && row.milestoneName) {
    return row.milestoneName;
  }
  if (
    (row.transactionType === "CASHED_OUT" ||
      row.transactionType === "STOCK_EXERCISE") &&
    row.eventName
  ) {
    return row.eventName;
  }
  return row.transactionType.replace(/_/g, " ");
}

function getEventSubtitle(row: EmployeeGrantTransactionDetail): string {
  if (
    row.transactionType === "VESTED" &&
    row.triggerType === TriggerType.EVENT
  ) {
    return "PERFORMANCE-BASED VESTING";
  }
  if (row.transactionType === "VESTED") {
    return "TIME-BASED SCHEDULE";
  }
  if (row.transactionType === "ACCELERATION") {
    return "ACCELERATION EVENT";
  }
  if (row.transactionType === "LAPSED") {
    if (row.milestoneName) return "MILESTONE NOT ACHIEVED";
    if (row.triggerType === TriggerType.EVENT)
      return "PERFORMANCE MILESTONE LAPSE";
    return "OPTION LAPSE";
  }
  if (row.transactionType === "SURRENDERED") {
    return "VOLUNTARY SURRENDER";
  }
  if (row.transactionType === "GRANTED") {
    return "GRANT ISSUED";
  }
  if (row.transactionType === "CASHED_OUT") {
    return "BUYBACK / CASH OUT";
  }
  if (row.transactionType === "STOCK_EXERCISE") {
    return "STOCK EXERCISE";
  }
  if (row.transactionType === "YET_TO_VEST") {
    return "ESTIMATED BASED ON CURRENT SCHEDULE";
  }
  if (row.transactionType === "FORFEITED") {
    return "OPTION FORFEITURE";
  }
  return row.transactionType.replace(/_/g, " ");
}

function getOptionsBadge(
  row: EmployeeGrantTransactionDetail,
  isFractional: boolean,
  currency: string
) {
  const absVal = Math.abs(row.options);
  const formatted = getFormattedValue(absVal, currency, isFractional);
  let sign = "+";
  let color = "rgb(80, 175, 245)";
  let label = "Vested";
  switch (row.transactionType) {
    case "VESTED":
    case "ACCELERATION":
      sign = "+";
      color = "rgb(80, 175, 245)";
      label = row.transactionType === "ACCELERATION" ? "Accelerated" : "Vested";
      break;
    case "GRANTED":
      sign = "+";
      color = "rgb(122, 208, 150)";
      label = "Granted";
      break;
    case "YET_TO_VEST":
    case "PROJECTED_VESTING":
      sign = "+";
      color = "rgb(245, 184, 50)";
      label = "Scheduled";
      break;
    case "CASHED_OUT":
    case "SURRENDERED":
      sign = "-";
      color =
        row.transactionType === "SURRENDERED"
          ? "rgb(215, 70, 60)"
          : "rgb(234, 88, 12)";
      label =
        row.transactionType === "SURRENDERED" ? "Surrendered" : "Cashed Out";
      break;
    case "LAPSED":
    case "FORFEITED":
    case "STOCK_EXERCISE":
      sign = "-";
      color =
        row.transactionType === "LAPSED"
          ? "rgb(110, 118, 132)"
          : row.transactionType === "FORFEITED"
          ? "rgb(148, 74, 74)"
          : "rgb(115, 80, 220)";
      label =
        row.transactionType === "LAPSED"
          ? "Adjusted"
          : row.transactionType === "FORFEITED"
          ? "Forfeited"
          : "Exercised";
      break;
    default:
      sign = "";
      color = "rgb(150, 150, 150)";
      label = "";
  }
  return { sign, color, formatted, label };
}

function getLedgerRowAccentColor(transactionType: string): string {
  switch (transactionType) {
    case "GRANTED":
      return "rgb(52, 168, 110)";
    case "VESTED":
      return "rgb(50, 145, 220)";
    case "ACCELERATION":
      return "rgb(175, 60, 210)";
    case "YET_TO_VEST":
    case "PROJECTED_VESTING":
      return "rgb(195, 138, 10)";
    case "SURRENDERED":
      return "rgb(215, 70, 60)";
    case "LAPSED":
      return "rgb(110, 118, 132)";
    case "FORFEITED":
      return "rgb(148, 74, 74)";
    case "CASHED_OUT":
      return "rgb(234, 88, 12)";
    case "SOLD":
      return "rgb(8, 145, 178)";
    case "STOCK_EXERCISE":
      return "rgb(115, 80, 220)";
    default:
      return "rgb(210, 214, 220)";
  }
}

const MONTHS = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

function LedgerView(props: {
  data: EmployeeGrantTransactionDetail[];
  currency: string;
  isFractional: boolean;
  employeeName: string;
}) {
  const { data, currency, isFractional, employeeName } = props;
  const [filter, setFilter] = useState<LedgerFilterValue>("all");
  const [search, setSearch] = useState("");
  const { mutate: exportExcel } = useExportToExcel();
  const { companyData } = useCompanyStore();
  const [isExporting, setIsExporting] = useState(false);

  const filtered = useMemo(() => {
    let rows = data.filter((r) => r.date !== "TOTAL");
    if (filter === "vested") {
      rows = rows.filter(
        (r) =>
          ["VESTED", "ACCELERATION"].includes(r.transactionType) ||
          isProjectedTransaction(r.transactionType)
      );
    } else if (filter === "acceleration") {
      rows = rows.filter((r) => r.transactionType === "ACCELERATION");
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.identifier?.toLowerCase().includes(q) ||
          r.transactionType.toLowerCase().includes(q) ||
          r.milestoneName?.toLowerCase().includes(q) ||
          r.eventName?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, filter, search]);

  // Group by year → month
  const grouped = useMemo(() => {
    const byYear: Record<
      string,
      Record<string, EmployeeGrantTransactionDetail[]>
    > = {};
    for (const row of filtered) {
      const d = new Date(row.date);
      const year = String(d.getFullYear());
      const month = MONTHS[d.getMonth()];
      if (!byYear[year]) byYear[year] = {};
      if (!byYear[year][month]) byYear[year][month] = [];
      byYear[year][month].push(row);
    }
    // Sort years descending, months by calendar order descending
    const sortedYears = Object.keys(byYear).sort(
      (a, b) => Number(b) - Number(a)
    );
    return sortedYears.map((year) => ({
      year,
      months: Object.keys(byYear[year])
        .sort((a, b) => MONTHS.indexOf(b) - MONTHS.indexOf(a))
        .map((month) => ({
          month,
          rows: byYear[year][month].sort((a, b) => {
            const dateDiff =
              new Date(a.date).getTime() - new Date(b.date).getTime();
            if (dateDiff !== 0) return dateDiff;
            // On the same date, sort by transaction type order
            const typeOrder: { [key: string]: number } = {
              GRANTED: 1,
              PROJECTED_VESTING: 2,
              YET_TO_VEST: 2,
              VESTED: 3,
              ACCELERATION: 4,
              CASHED_OUT: 4,
              STOCK_EXERCISE: 5,
              SOLD: 6,
              LAPSED: 7,
              SURRENDERED: 8,
              FORFEITED: 8,
              CASH_EXERCISE_REQUESTED: 9,
              STOCK_EXERCISE_REQUESTED: 10,
              VEST_REQUESTED: 11,
            };
            const pa = typeOrder[a.transactionType] ?? 99;
            const pb = typeOrder[b.transactionType] ?? 99;
            return pa - pb;
          }),
        })),
    }));
  }, [filtered]);

  const exportLedgerExcel = () => {
    setIsExporting(true);
    const excelData = filtered.map((row) => {
      const { sign } = getOptionsBadge(row, isFractional, currency);
      return {
        date: formatDisplayDate(row.date),
        projectedDate: row.projectedDate
          ? formatDisplayDate(row.projectedDate)
          : "",
        event: getEventTitle(row),
        status: getEventSubtitle(row),
        grantId: row.identifier ?? "",
        vestingBasis:
          row.triggerType === TriggerType.TIME
            ? "Time"
            : row.triggerType === TriggerType.EVENT
            ? "Event"
            : row.triggerType === TriggerType.BOTH
            ? "Both"
            : "",
        options: `${sign}${Math.abs(row.options)}`,
        netVested: row.netVested,
      };
    });
    const headers = [
      { key: "date", value: "Date" },
      { key: "projectedDate", value: "Projected Date" },
      { key: "event", value: "Event" },
      { key: "status", value: "Status" },
      { key: "grantId", value: "Grant ID" },
      { key: "vestingBasis", value: "Vesting Basis" },
      { key: "options", value: "No. of Options" },
      { key: "netVested", value: "Cumulative Net Vested" },
    ];
    exportExcel(
      {
        heading: "Equity Ledger",
        companyName: companyData?.name ?? "",
        data: excelData,
        headers,
        maskPiiData: false,
      },
      {
        onSuccess: async (res: any) => {
          const reportDto = res.data;
          const blob = await convertBase64ToBlob(
            reportDto.base64File ?? "",
            reportDto.fileType ??
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          );
          const safeName = (
            employeeName ||
            companyData?.name ||
            "export"
          ).replace(/\s+/g, "_");
          downloadBlobObject(blob, `Equity_Ledger_${safeName}`);
          setIsExporting(false);
        },
        onError: () => {
          setIsExporting(false);
        },
      }
    );
  };

  const printLedger = () => {
    const win = window.open("", "_blank", "width=1000,height=700");
    if (!win) return;

    const rowsHtml = grouped
      .map(({ year, months }) => {
        const monthBlocks = months
          .map(({ month, rows }) => {
            const rowHtml = rows
              .map((row) => {
                const {
                  sign,
                  formatted,
                  label,
                  color: badgeColor,
                } = getOptionsBadge(row, isFractional, currency);
                const isProjected = isProjectedTransaction(row.transactionType);
                const title = getEventTitle(row);
                const subtitle = getEventSubtitle(row);
                const vtColor =
                  row.triggerType === TriggerType.EVENT
                    ? "rgb(160,120,245)"
                    : row.triggerType === TriggerType.BOTH
                    ? "rgb(122,208,150)"
                    : "rgb(80,175,245)";
                const vtBg =
                  row.triggerType === TriggerType.EVENT
                    ? "rgb(240,230,255)"
                    : row.triggerType === TriggerType.BOTH
                    ? "rgb(232,255,243)"
                    : "rgb(230,245,255)";
                const vtLabel =
                  row.triggerType === TriggerType.TIME
                    ? "Time-based"
                    : row.triggerType === TriggerType.EVENT
                    ? "Event-based"
                    : row.triggerType === TriggerType.BOTH
                    ? "Time + Event"
                    : "";
                return `<tr style="border-bottom:1px solid #eee;opacity:${
                  isProjected ? 0.6 : 1
                }">
                  <td style="padding:7px 8px;font-size:11px;color:#555;white-space:nowrap">
                    ${formatDisplayDate(row.date)}
                    ${
                      row.projectedDate
                        ? `<br><span style="font-size:9px;color:#bbb">Sched. ${formatDisplayDate(
                            row.projectedDate
                          )}</span>`
                        : ""
                    }
                  </td>
                  <td style="padding:7px 8px;font-size:11px">
                    <div style="font-weight:600;color:#222;margin-bottom:3px">
                      ${title}
                      ${
                        !isProjected
                          ? `<span style="font-size:9px;font-weight:600;padding:2px 6px;border-radius:3px;background:${badgeColor}20;color:${badgeColor};margin-left:5px">${sign}${formatted} ${label}</span>`
                          : `<span style="font-size:9px;border:1px solid #f59e0b;color:#f59e0b;padding:1px 5px;border-radius:3px;margin-left:5px">SCHEDULED</span>`
                      }
                    </div>
                    <div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center">
                      <span style="font-size:8px;font-weight:600;padding:1px 5px;border-radius:3px;background:${badgeColor}18;color:${badgeColor}">${row.transactionType.replace(
                  /_/g,
                  " "
                )}</span>
                      ${
                        vtLabel
                          ? `<span style="font-size:8px;font-weight:600;padding:1px 5px;border-radius:3px;background:${vtBg};color:${vtColor}">${vtLabel}</span>`
                          : ""
                      }
                    </div>
                  </td>
                  <td style="padding:7px 8px;font-size:11px;color:#555;white-space:nowrap">
                    ${
                      row.identifier
                        ? `<span style="font-size:9px;font-family:monospace;background:#f3f4f6;padding:2px 6px;border-radius:3px;color:#555">${row.identifier}</span>`
                        : ""
                    }
                  </td>
                  <td style="padding:7px 8px;font-size:11px;text-align:right;font-weight:500;color:${badgeColor};white-space:nowrap">
                    ${sign}${getFormattedValue(
                  Math.abs(row.options),
                  currency,
                  isFractional
                )}
                  </td>
                  <td style="padding:7px 8px;font-size:11px;text-align:right;font-weight:700;color:#222;white-space:nowrap">
                    ${getFormattedValue(row.netVested, currency, isFractional)}
                  </td>
                </tr>`;
              })
              .join("");
            return `<tr style="background:#fff9f5"><td colspan="5" style="padding:8px 8px 4px;font-size:9px;font-weight:700;color:#f97316;text-transform:uppercase;letter-spacing:2px">${month}</td></tr>${rowHtml}`;
          })
          .join("");
        return `<tr><td colspan="5" style="padding:14px 8px 4px;font-size:22px;font-weight:300;color:#ccc">${year}</td></tr>${monthBlocks}`;
      })
      .join("");

    const html = `<!DOCTYPE html><html>
<head>
  <meta charset="UTF-8">
  <title>Equity Ledger</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 24px; color: #333; }
    h1 { font-size: 17px; font-weight: 600; margin: 0 0 3px 0; color: #111; }
    p.meta { font-size: 10px; color: #999; margin: 0 0 16px 0; }
    table { width: 100%; border-collapse: collapse; }
    thead th { font-size: 9px; font-weight: 700; color: #aaa; text-transform: uppercase; letter-spacing: 1px; padding: 6px 8px; border-bottom: 2px solid #e5e7eb; text-align: left; }
    thead th:nth-child(4), thead th:nth-child(5) { text-align: right; }
    tr { page-break-inside: avoid; }
    @media print { body { padding: 8px; } @page { margin: 0.8cm; } }
  </style>
</head>
<body>
  <h1>Equity Ledger</h1>
  <p class="meta">Printed on ${new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} &nbsp;|&nbsp; ${filtered.length} transaction${
      filtered.length !== 1 ? "s" : ""
    }</p>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Event &amp; Vesting Basis</th><th>Grant ID</th>
        <th style="text-align:right">No. of Options</th>
        <th style="text-align:right">Cumulative Net Vested</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</body></html>`;

    win.document.write(
      html.replace(
        "<title>Equity Ledger</title>",
        `<title>Equity_Ledger_${(
          employeeName ||
          companyData?.name ||
          "export"
        ).replace(/\s+/g, "_")}</title>`
      )
    );
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <VStack className="w-full gap-0">
      {/* Toolbar */}
      <HStack className="justify-between items-center mb-3 flex-wrap gap-2">
        <HStack className="gap-1">
          {LEDGER_FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-colors ${
                filter === tab.value
                  ? "bg-orange-501 text-white border-orange-501"
                  : "bg-white text-gray-500 border-gray-200 hover:border-orange-501 hover:text-orange-501"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </HStack>
        <HStack className="gap-2 items-center">
          <div className="flex items-center gap-1.5 border border-gray-200 rounded px-2 py-1 bg-white">
            <Icon icon="mdi:magnify" height={14} className="text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Grant ID or Event..."
              className="text-xs outline-none w-44 placeholder-gray-400"
            />
          </div>
          <button
            onClick={printLedger}
            title="Export as PDF"
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-gray-200 bg-white text-gray-500 hover:border-orange-501 hover:text-orange-501 transition-colors"
          >
            <Icon icon="mdi:file-pdf-box" height={14} />
            Export PDF
          </button>
          <button
            onClick={exportLedgerExcel}
            disabled={isExporting}
            title="Export as Excel"
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-gray-200 bg-white text-gray-500 hover:border-orange-501 hover:text-orange-501 transition-colors disabled:opacity-50"
          >
            <Icon icon="mdi:microsoft-excel" height={14} />
            {isExporting ? "Exporting…" : "Export Excel"}
          </button>
        </HStack>
      </HStack>

      {/* Scrollable body with sticky header */}
      <div className="overflow-y-auto max-h-[600px] relative">
        {/* Column headers — sticky */}
        <div className="sticky top-0 z-10 bg-white grid grid-cols-[88px_minmax(260px,1.2fr)_156px_78px_96px_120px] gap-x-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-2 py-2 border-b border-gray-200 shadow-sm">
          <span>Date</span>
          <span>Event &amp; Vesting Basis</span>
          <span>Running Balance</span>
          <span>Grant ID</span>
          <span className="text-right">No. of Options</span>
          <span className="text-right">Cumulative Net Vested</span>
        </div>

        {/* Rows */}
        {grouped.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-8">
            No transactions found.
          </p>
        )}
        {grouped.map(({ year, months }) => (
          <div key={year} className="w-full">
            {/* Year separator */}
            <div className="flex items-center gap-3 py-5 px-2 select-none">
              <span className="text-2xl font-extralight text-gray-300 tabular-nums">
                {year}
              </span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
            {months.map(({ month, rows }) => (
              <div key={month} className="w-full">
                {/* Month separator */}
                <div className="flex items-center gap-2 px-2 pb-2 pt-1">
                  <span className="text-[10px] font-semibold text-orange-501 tracking-widest uppercase">
                    {month}
                  </span>
                  <span className="text-[9px] font-medium bg-orange-50 text-orange-400 border border-orange-200 rounded-full px-1.5 py-px">
                    {rows.length}
                  </span>
                </div>
                {rows.map((row, idx) => {
                  const { icon, color } = getTransactionIcon(
                    row.transactionType,
                    row.triggerType
                  );
                  const {
                    sign,
                    color: badgeColor,
                    formatted,
                    label,
                  } = getOptionsBadge(row, isFractional, currency);
                  const isProjected = isProjectedTransaction(
                    row.transactionType
                  );
                  return (
                    <div
                      key={`${row.date}-${row.identifier}-${idx}`}
                      className={`grid grid-cols-[88px_minmax(260px,1.2fr)_156px_78px_96px_120px] gap-x-1.5 items-start py-4 px-3 border-b border-dashed border-gray-100 hover:bg-slate-50 transition-colors ${
                        isProjected ? "opacity-60" : ""
                      }`}
                      style={{
                        borderLeft: `3px solid ${getLedgerRowAccentColor(
                          row.transactionType
                        )}`,
                      }}
                    >
                      {/* Date */}
                      <div className="flex flex-col">
                        <span
                          className={`text-xl font-extralight tabular-nums leading-none ${
                            isProjected ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {format(new Date(row.date), "dd")}
                        </span>
                        <span
                          className={`text-[9px] font-medium uppercase tracking-wider mt-0.5 ${
                            isProjected ? "text-gray-300" : "text-gray-400"
                          }`}
                        >
                          {format(new Date(row.date), "MMM yyyy")}
                        </span>
                        {row.transactionType === "ACCELERATION" &&
                        row.acceleratedFromDate ? (
                          <span
                            className="text-[9px] mt-1.5 italic"
                            style={{ color: "rgb(175, 60, 210)" }}
                          >
                            from{" "}
                            {format(
                              new Date(row.acceleratedFromDate),
                              "dd MMM"
                            )}
                          </span>
                        ) : row.projectedDate ? (
                          <span className="text-[9px] text-amber-500 mt-1.5 italic">
                            projected{" "}
                            {format(new Date(row.projectedDate), "dd MMM")}
                          </span>
                        ) : null}
                      </div>

                      {/* Event & Vesting Basis */}
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="flex-shrink-0 mt-1">
                          <Icon icon={icon} height={16} style={{ color }} />
                        </div>
                        <div className="flex flex-col min-w-0 gap-2">
                          {/* Title + options count */}
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span
                              className={`text-sm font-semibold leading-tight ${
                                isProjected ? "text-gray-400" : "text-gray-800"
                              }`}
                            >
                              {getEventTitle(row)}
                            </span>
                            {isProjected ? (
                              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 text-amber-500 uppercase tracking-wide whitespace-nowrap">
                                Scheduled
                              </span>
                            ) : (
                              <span
                                className="text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap"
                                style={{
                                  color: badgeColor,
                                  backgroundColor: `${badgeColor}15`,
                                }}
                              >
                                {sign}
                                {formatted} {label}
                              </span>
                            )}
                          </div>
                          {/* Milestone / event name if present */}
                          {(row.milestoneName || row.eventName) && (
                            <span className="text-xs text-gray-500 leading-tight -mt-1">
                              {row.milestoneName ?? row.eventName}
                            </span>
                          )}
                          {/* Pills row */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <TransactionLabel
                              transactionType={row.transactionType}
                              label={row.transactionType.replace(/_/g, " ")}
                              inline
                            />
                            {row.triggerType && (
                              <span
                                className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap"
                                style={{
                                  color:
                                    row.triggerType === TriggerType.EVENT
                                      ? "rgb(115, 80, 220)"
                                      : row.triggerType === TriggerType.BOTH
                                      ? "rgb(52, 168, 110)"
                                      : "rgb(50, 145, 220)",
                                  backgroundColor:
                                    row.triggerType === TriggerType.EVENT
                                      ? "rgb(235, 228, 255)"
                                      : row.triggerType === TriggerType.BOTH
                                      ? "rgb(225, 252, 239)"
                                      : "rgb(220, 240, 255)",
                                }}
                              >
                                {row.triggerType === TriggerType.TIME
                                  ? "Time-based"
                                  : row.triggerType === TriggerType.EVENT
                                  ? "Event-based"
                                  : "Time + Event"}
                              </span>
                            )}
                          </div>
                          {/* Contextual stats */}
                          {row.transactionType === "GRANTED" && (
                            <div className="text-[9px] text-gray-400 mt-0.5">
                              Grant total:{" "}
                              <span className="font-semibold text-gray-500">
                                {getFormattedValue(
                                  row.totalGranted,
                                  currency,
                                  isFractional
                                )}
                              </span>
                            </div>
                          )}
                          {row.transactionType === "STOCK_EXERCISE" &&
                            row.netStockExercised > 0 && (
                              <div className="text-[9px] text-gray-400 mt-0.5">
                                Cumulative exercised:{" "}
                                <span className="font-semibold text-gray-500">
                                  {getFormattedValue(
                                    row.netStockExercised,
                                    currency,
                                    isFractional
                                  )}
                                </span>
                              </div>
                            )}
                          {row.transactionType === "CASHED_OUT" &&
                            row.netCashedOut > 0 && (
                              <div className="text-[9px] text-gray-400 mt-0.5">
                                Cumulative cashed out:{" "}
                                <span className="font-semibold text-gray-500">
                                  {getFormattedValue(
                                    row.netCashedOut,
                                    currency,
                                    isFractional
                                  )}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Running Balance */}
                      <div className="rounded-md border border-blue-100 bg-blue-50/50 px-2.5 py-2 flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-1">
                          <span className="flex items-center gap-1 text-[9px] font-medium text-emerald-600 uppercase tracking-wide">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                            Granted
                          </span>
                          <span className="text-[10px] font-bold text-emerald-700 tabular-nums">
                            {getFormattedValue(
                              row.totalGranted,
                              currency,
                              isFractional
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-1">
                          <span className="flex items-center gap-1 text-[9px] font-medium text-blue-600 uppercase tracking-wide">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                            Vested
                          </span>
                          <span className="text-[10px] font-bold text-blue-700 tabular-nums">
                            {getFormattedValue(
                              row.currentVested,
                              currency,
                              isFractional
                            )}
                          </span>
                        </div>
                        {row.netStockExercised > 0 && (
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex items-center gap-1 text-[9px] font-medium text-violet-600 uppercase tracking-wide">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
                              Exercised
                            </span>
                            <span className="text-[10px] font-bold text-violet-700 tabular-nums">
                              {getFormattedValue(
                                row.netStockExercised,
                                currency,
                                isFractional
                              )}
                            </span>
                          </div>
                        )}
                        {row.netCashedOut > 0 && (
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex items-center gap-1 text-[9px] font-medium text-orange-600 uppercase tracking-wide">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                              Cashed Out
                            </span>
                            <span className="text-[10px] font-bold text-orange-700 tabular-nums">
                              {getFormattedValue(
                                row.netCashedOut,
                                currency,
                                isFractional
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Grant ID */}
                      <div className="flex items-start pt-0.5">
                        {row.identifier && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">
                            {row.identifier}
                          </span>
                        )}
                      </div>

                      {/* No. of Options */}
                      <div className="text-right">
                        <span
                          className="text-xs font-medium tabular-nums"
                          style={{ color: badgeColor }}
                        >
                          {sign}
                          {getFormattedValue(
                            Math.abs(row.options),
                            currency,
                            isFractional
                          )}
                        </span>
                      </div>

                      {/* Cumulative Net Vested */}
                      <div className="text-right">
                        {isProjected ? (
                          <span className="text-xs text-gray-300">—</span>
                        ) : (
                          <span className="text-xs font-semibold text-gray-800 tabular-nums">
                            {getFormattedValue(
                              row.netVested,
                              currency,
                              isFractional
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}

        {/* Footer count */}
        {filtered.length > 0 && (
          <p className="text-[10px] text-gray-400 pt-3 px-2 pb-2">
            Showing {filtered.length} transaction
            {filtered.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>
    </VStack>
  );
}

function AGGridEmployeeScheduleTable(props: {
  data: EmployeeGrantTransactionDetail[];
  currency: string;
  isFractional: boolean;
  employeeName: string;
}) {
  const { data, currency, isFractional, employeeName } = props;
  const employeeVestingData = useMemo(() => {
    if (data) {
      return data;
    }
    return [];
  }, [data]);
  const [pinnedRowData, setPinnedRowData] = useState([{ transactionType: "" }]);
  const [groupBy, setGroupBy] = useState<
    "none" | "grantId" | "transactionType"
  >("none");
  const [isExporting, setIsExporting] = useState(false);
  const { mutate: exportExcel } = useExportToExcel();
  const { companyData } = useCompanyStore();
  const grandTotalRow = "bottom";
  const gridApi = useRef<GridApi | any>(null);
  const popupParent = useMemo(
    () => (typeof document !== "undefined" ? document.body : undefined),
    []
  );
  const onGridReady = (params: any) => {
    gridApi.current = params;
    recalculateTotalRow();
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"],
    }),
    []
  );
  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      minWidth: 220,
      wrapText: true,
      autoHeight: true,
      cellRendererParams: {
        suppressCount: false,
        innerRenderer: (param: any) => {
          const value: string = param.value ?? "";
          // Group by transactionType — render as a TransactionLabel badge
          if (groupBy === "transactionType") {
            return (
              <TransactionLabel
                transactionType={value}
                label={value.replace(/_/g, " ")}
                inline
              />
            );
          }
          return <span>{value}</span>;
        },
      },
    }),
    [groupBy]
  );
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "DATE",
        field: "date",
        sortable: true,
        autoHeight: false,
        wrapText: false,
        minWidth: 200,
        width: 200,
        filterParams: {
          maxNumConditions: 5,
          inRangeInclusive: true,
          buttons: ["reset"],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (!cellValue) {
              return -1;
            }

            const filterDate = new Date(filterLocalDateAtMidnight);
            filterDate.setHours(0, 0, 0, 0);

            const cellDate = new Date(cellValue);
            cellDate.setHours(0, 0, 0, 0);

            const filterEpoch = filterDate.getTime();
            const cellEpoch = cellDate.getTime();

            if (cellEpoch < filterEpoch) {
              return -1;
            } else if (cellEpoch > filterEpoch) {
              return 1;
            }
            return 0;
          },
        },
        filter: "agDateColumnFilter",
        comparator: (a: any, b: any) =>
          new Date(a).getTime() - new Date(b).getTime(),
        suppressAutoSize: false,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (param: any) => {
          const row: EmployeeGrantTransactionDetail = param.data;
          if (!row) return null;
          if (row.date === "TOTAL") return <span>TOTAL</span>;
          const isAcceleration = row.transactionType === "ACCELERATION";
          const hasDeviation =
            !isAcceleration &&
            row.projectedDate &&
            getDateKey(row.date) !== getDateKey(row.projectedDate);
          return (
            <div className="flex flex-col leading-tight gap-1">
              <span>{formatDisplayDate(row.date)}</span>
              {isAcceleration && row.acceleratedFromDate && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded w-fit"
                  style={{
                    backgroundColor: "rgb(245, 230, 255)",
                    color: "rgb(175, 60, 210)",
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "rgb(175, 60, 210)" }}
                  />
                  From: {formatDisplayDate(row.acceleratedFromDate)}
                </span>
              )}
              {hasDeviation && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded w-fit"
                  style={{
                    backgroundColor: "rgb(255, 243, 210)",
                    color: "rgb(195, 138, 10)",
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "rgb(195, 138, 10)" }}
                  />
                  Proj: {formatDisplayDate(row.projectedDate!)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: "GRANT ID",
        field: "identifier",
        autoHeight: true,
        wrapText: true,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        sortable: true,
        rowGroup: groupBy === "grantId",
        hide: groupBy === "grantId",
      },
      {
        headerName: "TOTAL GRANTED",
        field: "totalGranted",
        autoHeight: true,
        wrapText: true,
        sortable: true,
        filter: "agNumberColumnFilter",
        aggFunc: "max",
        valueFormatter: (e) =>
          getFormattedValue(e.value, currency, isFractional),
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "TRANSACTION TYPE",
        field: "transactionType",
        autoHeight: true,
        wrapText: true,
        sortable: true,
        filter: "agSetColumnFilter",
        rowGroup: groupBy === "transactionType",
        hide: false,
        minWidth: 180,
        width: 180,
        filterValueGetter: (param) =>
          param.data?.transactionType?.replace(/_/g, " "),
        cellRenderer: (param: any) => {
          const row: EmployeeGrantTransactionDetail = param.data;
          if (!row) return null;
          let displayLabel = row.transactionType.replace(/_/g, " ");
          if (
            row.transactionType === "VESTED" &&
            row.triggerType === TriggerType.EVENT &&
            row.milestoneName
          ) {
            displayLabel = "VESTED";
          } else if (row.transactionType === "LAPSED" && row.milestoneName) {
            displayLabel = "LAPSED";
          }
          return (
            <div className="flex items-center py-2 w-full">
              <TransactionLabel
                transactionType={row.transactionType}
                label={displayLabel}
              />
            </div>
          );
        },
      },
      {
        headerName: "DETAILS",
        field: "milestoneName",
        autoHeight: true,
        wrapText: true,
        sortable: false,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterValueGetter: (param) => {
          const row: EmployeeGrantTransactionDetail = param.data;
          if (!row) return "";
          const parts: string[] = [];
          const showTriggerType =
            row.triggerType &&
            row.transactionType !== "GRANTED" &&
            row.transactionType !== "LAPSED" &&
            row.transactionType !== "SURRENDERED";
          if (showTriggerType) {
            parts.push(
              row.triggerType === TriggerType.TIME
                ? "Time-based"
                : row.triggerType === TriggerType.EVENT
                ? "Event-based"
                : "Time + Event"
            );
          }
          if (row.milestoneName) {
            parts.push(row.milestoneName);
          }
          if (
            (row.transactionType === "CASHED_OUT" ||
              row.transactionType === "STOCK_EXERCISE") &&
            row.eventName
          ) {
            parts.push(row.eventName);
          }
          return parts.join(" ");
        },
        minWidth: 200,
        cellRenderer: (param: any) => {
          const row: EmployeeGrantTransactionDetail = param.data;
          if (!row) return null;

          const showTriggerType =
            row.triggerType &&
            row.transactionType !== "GRANTED" &&
            row.transactionType !== "LAPSED" &&
            row.transactionType !== "SURRENDERED";

          const milestoneLine = row.milestoneName ?? null;

          const eventLine =
            (row.transactionType === "CASHED_OUT" ||
              row.transactionType === "STOCK_EXERCISE") &&
            row.eventName
              ? row.eventName
              : null;

          const hasAny = showTriggerType || milestoneLine || eventLine;
          if (!hasAny) return <span className="text-gray-300 text-xs">—</span>;

          return (
            <div className="flex flex-col justify-center gap-2 py-2">
              {showTriggerType && (
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                    style={{
                      color:
                        row.triggerType === TriggerType.EVENT
                          ? "rgb(115, 80, 220)"
                          : row.triggerType === TriggerType.BOTH
                          ? "rgb(52, 168, 110)"
                          : "rgb(50, 145, 220)",
                      backgroundColor:
                        row.triggerType === TriggerType.EVENT
                          ? "rgb(235, 228, 255)"
                          : row.triggerType === TriggerType.BOTH
                          ? "rgb(225, 252, 239)"
                          : "rgb(220, 240, 255)",
                    }}
                  >
                    {row.triggerType === TriggerType.TIME
                      ? "Time-based"
                      : row.triggerType === TriggerType.EVENT
                      ? "Event-based"
                      : "Time + Event"}
                  </span>
                </div>
              )}
              {milestoneLine && (
                <div className="flex items-start gap-1.5">
                  <span className="text-xxs text-gray-700 leading-tight font-medium">
                    {milestoneLine}
                  </span>
                </div>
              )}
              {eventLine && (
                <div className="flex items-start gap-1.5">
                  <span className="text-xxs text-gray-700 leading-tight font-medium">
                    {eventLine}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: "UNITS THIS EVENT",
        field: "options",
        autoHeight: true,
        wrapText: true,
        sortable: true,
        filter: "agNumberColumnFilter",
        headerTooltip: "Number of options transacted in this single event",
        cellRenderer: (param: any) => {
          const row: EmployeeGrantTransactionDetail = param.data;
          if (!row) return null;
          const absVal = Math.abs(row.options);
          const { color, sign } = getOptionsBadge(row, isFractional, currency);
          const formatted = getFormattedValue(absVal, currency, isFractional);
          return (
            <span style={{ color, fontWeight: 500 }}>
              {sign}
              {formatted}
            </span>
          );
        },
      },
      {
        headerName: "CUMULATIVE NET VESTED",
        field: "netVested",
        autoHeight: true,
        wrapText: true,
        sortable: true,
        aggFunc: "max",
        valueFormatter: (e) =>
          getFormattedValue(e.value, currency, isFractional),
        filter: "agNumberColumnFilter",
      },
    ],
    [groupBy]
  );
  const recalculateTotalRow = () => {
    if (gridApi.current) {
      const rowData: EmployeeGrantTransactionDetail[] = [];
      gridApi.current.api.forEachNodeAfterFilterAndSort(
        (node: { data: EmployeeGrantTransactionDetail }) => {
          if (node.data) {
            rowData.push(node.data);
          }
        }
      );
      const totalRow: EmployeeGrantTransactionDetail = {
        currentVested: _.sumBy(rowData, (data) => data.currentVested),
        date: "TOTAL",
        transactionType: "TOTAL",
        options: 0,
        netCashedOut:
          _.maxBy(rowData, (data) => data.netCashedOut)?.netCashedOut || 0,
        totalTransactedOptions: 0,
        netStockExercised: 0,
        identifier: `${new Set(rowData.map((data) => data.identifier)).size}`,
        netVested: 0,
        totalGranted:
          _.maxBy(
            rowData.filter((data) => data.transactionType === "GRANTED"),
            (data) => data.totalGranted
          )?.totalGranted || 0,
      };
    }
  };
  const exportTableExcel = () => {
    setIsExporting(true);
    const excelData = employeeVestingData.map((row) => {
      const { sign } = getOptionsBadge(row, isFractional, currency);
      const title = getEventTitle(row);
      const subtitle = getEventSubtitle(row);
      return {
        date: formatDisplayDate(row.date),
        projectedDate: row.projectedDate
          ? formatDisplayDate(row.projectedDate)
          : "",
        grantId: row.identifier ?? "",
        totalGranted: row.totalGranted,
        event: title || subtitle,
        transactionType: row.transactionType.replace(/_/g, " "),
        milestone: row.milestoneName ?? row.eventName ?? "",
        options: `${sign}${Math.abs(row.options)}`,
        netVested: row.netVested,
      };
    });
    const headers = [
      { key: "date", value: "Date" },
      { key: "projectedDate", value: "Projected Date" },
      { key: "grantId", value: "Grant ID" },
      { key: "totalGranted", value: "Total Granted" },
      { key: "event", value: "Event" },
      { key: "transactionType", value: "Transaction Type" },
      { key: "milestone", value: "Milestone / Event" },
      { key: "options", value: "Units This Event" },
      { key: "netVested", value: "Cumulative Net Vested" },
    ];
    exportExcel(
      {
        heading: "Vesting Schedule",
        companyName: companyData?.name ?? "",
        data: excelData,
        headers,
        maskPiiData: false,
      },
      {
        onSuccess: async (res: any) => {
          const reportDto = res.data;
          const blob = await convertBase64ToBlob(
            reportDto.base64File ?? "",
            reportDto.fileType ??
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          );
          const safeName = (
            employeeName ||
            companyData?.name ||
            "export"
          ).replace(/\s+/g, "_");
          downloadBlobObject(blob, `Vesting_Schedule_${safeName}`);
          setIsExporting(false);
        },
        onError: () => {
          setIsExporting(false);
        },
      }
    );
  };

  const groupByOptions: {
    value: "none" | "grantId" | "transactionType";
    label: string;
  }[] = [
    { value: "none", label: "None" },
    { value: "grantId", label: "Grant ID" },
    { value: "transactionType", label: "Transaction Type" },
  ];
  return (
    <VStack className="w-full gap-2">
      <HStack className="justify-between items-center gap-2">
        <HStack className="gap-2 items-center">
          <span className="text-xs text-gray-500 font-medium">Group by:</span>
          {groupByOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setGroupBy(opt.value)}
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                groupBy === opt.value
                  ? "bg-orange-501 text-white border-orange-501"
                  : "bg-white text-gray-600 border-gray-300 hover:border-orange-501 hover:text-orange-501"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </HStack>
        <button
          onClick={exportTableExcel}
          disabled={isExporting}
          title="Export as Excel"
          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-gray-200 bg-white text-gray-500 hover:border-orange-501 hover:text-orange-501 transition-colors disabled:opacity-50"
        >
          <Icon icon="mdi:microsoft-excel" height={14} />
          {isExporting ? "Exporting…" : "Export Excel"}
        </button>
      </HStack>
      <HStack className="justify-between w-full ">
        <Box
          style={{
            height: `${Math.min(
              400,
              (employeeVestingData.length >= 10
                ? 10
                : employeeVestingData.length + 3) * 60
            )}px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            rowData={employeeVestingData}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDisplayType="singleColumn"
            groupDefaultExpanded={0}
            suppressAggFuncInHeader={true}
            suppressMenuHide={true}
            popupParent={popupParent}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            getRowStyle={(params) => {
              const isRowPinned = params?.node?.isRowPinned();
              return {
                background: isRowPinned ? "#f0f0f0" : "",
                fontWeight: isRowPinned ? "normal" : "normal",
                color: isRowPinned ? "black" : "",
              };
            }}
            onGridReady={onGridReady}
          />
        </Box>
      </HStack>

      {/* ── How to read this table ── */}
      <div className="mt-1 border border-dashed border-gray-200 rounded-lg p-3 bg-gray-50/60">
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2.5">
          How to read this table
        </p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[10px] text-gray-500 leading-relaxed">
          <span>
            <strong className="text-gray-700">DATE</strong>: Actual transaction
            date. An amber &quot;Proj.&quot; badge means the event was
            rescheduled from its original scheduled date.
          </span>
          <span>
            <strong className="text-gray-700">GRANT ID</strong>: The grant this
            row belongs to. Use &quot;Group by → Grant ID&quot; to see all
            events grouped per grant.
          </span>
          <span>
            <strong className="text-gray-700">TOTAL GRANTED</strong>: Running
            cumulative units ever granted (high-water mark across all grants at
            this point in time).
          </span>
          <span>
            <strong className="text-gray-700">TRANSACTION TYPE</strong>: Nature
            of the event: VESTED, GRANTED, CASHED OUT, STOCK EXERCISE, LAPSED,
            SURRENDERED, or YET TO VEST (future projected schedule).
          </span>
          <span>
            <strong className="text-gray-700">DETAILS</strong>: Vesting trigger
            (Time / Event / Both), milestone name, or buyback event name when
            applicable.
          </span>
          <span>
            <strong className="text-gray-700">UNITS THIS EVENT</strong>: Units
            transacted in this single event only. Positive (+) for grants &
            vests; negative (−) for exercises, cashouts, lapses &amp;
            surrenders.
          </span>
          <span>
            <strong className="text-gray-700">CUMULATIVE NET VESTED</strong>:
            Running net vested balance after accounting for all exercises,
            cashouts, and surrenders up to and including this row.
          </span>
        </div>
      </div>
    </VStack>
  );
}

const TransactionLabel = (props: {
  transactionType: string;
  label?: string;
  inline?: boolean;
}) => {
  const { transactionType, label, inline } = props;
  const getLabelStyle = () => {
    const normalizedType = transactionType
      ?.replaceAll("_", " ")
      .toLowerCase()
      ?.trim();
    switch (normalizedType) {
      case "granted":
        return { bg: "rgb(225, 252, 239)", text: "rgb(52, 168, 110)" };
      case "vested":
        return { bg: "rgb(220, 240, 255)", text: "rgb(50, 145, 220)" };
      case "yet to vest":
        return { bg: "rgb(255, 243, 210)", text: "rgb(195, 138, 10)" };
      case "projected vesting":
        return { bg: "rgb(255, 243, 210)", text: "rgb(195, 138, 10)" };
      case "surrendered":
        return { bg: "rgb(255, 230, 228)", text: "rgb(215, 70, 60)" };
      case "lapsed":
        return { bg: "rgb(242, 243, 245)", text: "rgb(110, 118, 132)" };
      case "forfeited":
        return { bg: "rgb(248, 229, 229)", text: "rgb(148, 74, 74)" };
      case "cashed out":
        return { bg: "rgb(255, 237, 213)", text: "rgb(234, 88, 12)" };
      case "sold":
        return { bg: "rgb(207, 250, 254)", text: "rgb(8, 145, 178)" };
      case "stock exercise":
        return { bg: "rgb(235, 228, 255)", text: "rgb(115, 80, 220)" };
      case "acceleration":
        return { bg: "rgb(255, 235, 255)", text: "rgb(175, 60, 210)" };
      default:
        return { bg: "rgb(225, 252, 239)", text: "rgb(52, 168, 110)" };
    }
  };
  const { bg, text } = getLabelStyle();
  const displayText = label ?? transactionType.replace(/_/g, " ");

  // Compact inline variant — used in ledger view pills
  if (inline) {
    return (
      <span
        className="inline-flex items-center capitalize rounded text-xs px-1.5 py-0.5 whitespace-nowrap"
        style={{ color: text, backgroundColor: bg }}
      >
        {displayText}
      </span>
    );
  }

  // Full-width pill variant — used in AG Grid table cells
  return (
    <span
      className="flex items-center justify-center w-full rounded text-xs font-medium px-3 py-1.5 uppercase tracking-wider"
      style={{ color: text, backgroundColor: bg }}
    >
      {displayText}
    </span>
  );
};

export default VestingSchedule;
